/**
 * Pending Engine Result
 * Remembers, for this tab, the operation whose successful face result the
 * browser received but Portal has not yet confirmed. After a reload the
 * customer returns to waiting for that result instead of being asked to
 * capture again. It only chooses which screen to show; Portal alone decides
 * the outcome.
 */

import { storageService, StorageKeys } from '../services/StorageService';

// Longer than any session, so it never outlives the attempt it describes.
const PENDING_RESULT_MAX_AGE_MS = 15 * 60 * 1000;

interface PendingEngineResult {
  sessionId: string;
  operationId: string;
  operationGeneration: number;
}

interface OperationProjection {
  sessionId: string;
  attempt: null | {
    operation: null | { id: string; generation: number; acceptsResult?: boolean };
  };
}

export function rememberPendingEngineResult(result: PendingEngineResult): void {
  storageService.setItem(StorageKeys.PENDING_ENGINE_RESULT, result, { fallbackToMemory: true });
}

export function clearPendingEngineResult(): void {
  storageService.removeItem(StorageKeys.PENDING_ENGINE_RESULT, { fallbackToMemory: true });
}

/** The remembered result is for this exact operation and Portal can still record it. */
export function awaitsRememberedResult(projection: OperationProjection): boolean {
  const pending = storageService.getItem<PendingEngineResult>(
    StorageKeys.PENDING_ENGINE_RESULT,
    { fallbackToMemory: true, maxAge: PENDING_RESULT_MAX_AGE_MS },
  );
  const operation = projection.attempt?.operation;
  return (
    !!pending &&
    !!operation &&
    operation.acceptsResult === true &&
    pending.sessionId === projection.sessionId &&
    pending.operationId === operation.id &&
    pending.operationGeneration === operation.generation
  );
}
